import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ICustomerSiteLogAssetChecklistItemModel, ICustomerSiteLogChecklistModel } from "@models";
import { CustomerSiteLogService, GlobalsService, SleepService } from "@services";
import { SlickToastService } from "@slick-components";
import { SiteLogAssetPhotosDialogComponent } from "../site-log-asset-photos-dialog/site-log-asset-photos-dialog.component";

@Component({
	selector: "site-log-checklist-edit",
	templateUrl: "site-log-checklist-edit.component.html",
	styleUrls: ["site-log-checklist-edit.component.scss"],
	providers: [CustomerSiteLogService, SlickToastService]
})
export class SiteLogChecklistEditComponent implements OnChanges {
	@Input() customerId: number;
	@Input() jobId: number;
	@Input() appointmentId: number;
	@Output() onClose = new EventEmitter<void>();
	@Output() onSaved = new EventEmitter<void>();
	@ViewChild("assetPhotosDialogRef") assetPhotosDialogRef: SiteLogAssetPhotosDialogComponent;

	checklistModel: ICustomerSiteLogChecklistModel;
	private activePhotoItem: ICustomerSiteLogAssetChecklistItemModel;
	private activePhotoType: "Before" | "After" | "Damage";
	isLoading = false;
	spinnerStatus: string;
	private defaultUserName: string;

	constructor(
		private customerSiteLogService: CustomerSiteLogService,
		private slickToastService: SlickToastService) {
		this.defaultUserName = GlobalsService.userInfo?.fullName ?? "";
	}

	async ngOnChanges(changes: SimpleChanges) {
		if (!this.customerId || !this.jobId)
			return;

		const idsChanged = ["customerId", "jobId", "appointmentId"].some(key => changes[key]);
		if (idsChanged)
			await this.loadChecklist();
	}

	async loadChecklist() {
		this.checklistModel = null;
		this.isLoading = true;
		try {
			this.checklistModel = await this.customerSiteLogService.getSiteLogChecklist(
				this.customerId, this.jobId, this.appointmentId);
		} finally {
			this.isLoading = false;
		}
	}

	onCompletedChange(item: ICustomerSiteLogAssetChecklistItemModel) {
		if (item.isCompleted) {
			if (!item.completedBy?.trim())
				item.completedBy = this.defaultUserName;
			if (!item.completedDate)
				item.completedDate = new Date();
		} else {
			item.completedBy = "";
			item.completedDate = null;
		}
	}

	async onSave() {
		if (!this.checklistModel)
			return;

		try {
			this.spinnerStatus = "spin";
			await SleepService.sleep(200);
			this.checklistModel = await this.customerSiteLogService.saveSiteLogChecklist(
				this.customerId, this.jobId, this.appointmentId, this.checklistModel);
			this.spinnerStatus = "ok";
			this.slickToastService.showSuccess("Site Log saved.");
			this.onSaved.emit();
			this.onClose.emit();
		} catch {
			this.spinnerStatus = "error";
		}
	}

	onCancel() {
		this.onClose.emit();
	}

	async openPhotos(item: ICustomerSiteLogAssetChecklistItemModel, photoType: "Before" | "After" | "Damage") {
		const customerDocumentKey = this.checklistModel?.customerDocumentKey;
		if (!customerDocumentKey || !item?.documentKey) {
			this.slickToastService.showWarning("Customer document key is missing. Save the customer and reload Site Log.");
			return;
		}

		this.activePhotoItem = item;
		this.activePhotoType = photoType;
		if (!this.jobId) {
			this.slickToastService.showWarning("Job is required to add site log photos.");
			return;
		}

		await this.assetPhotosDialogRef.open(this.jobId, customerDocumentKey, item.documentKey, item.name, photoType);
	}

	onPhotoCountChanged(count: number) {
		if (!this.activePhotoItem || !this.activePhotoType)
			return;

		switch (this.activePhotoType) {
			case "Before":
				this.activePhotoItem.beforePhotoCount = count;
				break;
			case "After":
				this.activePhotoItem.afterPhotoCount = count;
				break;
			case "Damage":
				this.activePhotoItem.damagePhotoCount = count;
				break;
		}
	}

	photoButtonLabel(count: number, defaultLabel: string): string {
		return count > 0 ? `${defaultLabel} (${count})` : defaultLabel;
	}

	get progressPercent(): number {
		const p = this.checklistModel?.percentComplete ?? 0;
		return Math.min(100, Math.max(0, Math.round(p)));
	}

	get progressRingBackground(): string {
		return `conic-gradient(#198754 ${this.progressPercent}%, #dee2e6 0)`;
	}
}
